export interface StudyPageData {
  title: string;
  scripture: string;
  date?: string;
  bulletinSummary: string;
  quotes: string[];
  studyQuestions?: string[];
  mainPoints?: string[];
  keyScriptures?: string[];
  practicalApplications?: string[];
  prayerPoints?: string[];
  discussionQuestions?: string[];
  youtubeUrl?: string;
  churchName?: string;
}

function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function generateStudyPageHTML(data: StudyPageData): string {
  const youtubeId = data.youtubeUrl ? extractYouTubeId(data.youtubeUrl) : null;
  
  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sermon Study: ${data.title}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,700;1,400&family=Montserrat:wght@300;400;600;700&family=Outfit:wght@300;400;600&display=swap" rel="stylesheet">
    <style>
        :root {
            --parchment: #FDFBF7;
            --slate-800: #1E293B;
            --slate-900: #0F172A;
            --sage-50: #F0F4F1;
            --sage-100: #E1E9E4;
            --sage-600: #4A675D;
            --sage-800: #2D3E38;
            --gold-500: #B59410;
            --gold-600: #947A0D;
            --accent: #3E5C76;
        }
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body {
            font-family: 'Outfit', sans-serif;
            background-color: var(--parchment);
            color: var(--sage-800);
            line-height: 1.7;
            scroll-behavior: smooth;
        }
        
        .font-serif { font-family: 'Lora', serif; }
        .font-display { font-family: 'Montserrat', sans-serif; }
        
        .header {
            position: fixed;
            top: 0;
            width: 100%;
            z-index: 50;
            background: rgba(253, 251, 247, 0.8);
            backdrop-filter: blur(12px);
            border-bottom: 1px solid var(--sage-100);
        }
        
        .print-btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            background: var(--sage-600);
            color: white;
            border: none;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s;
        }
        
        .print-btn:hover {
            background: var(--sage-800);
        }
        
        .print-btn svg {
            width: 1rem;
            height: 1rem;
        }
        
        .header-inner {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 1.5rem;
            height: 4rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        
        .logo {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        
        .logo-icon {
            width: 2rem;
            height: 2rem;
            background: var(--sage-600);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .logo-icon svg { width: 1.25rem; height: 1.25rem; color: white; }
        
        .logo-text {
            font-family: 'Montserrat', sans-serif;
            font-weight: 700;
            letter-spacing: -0.025em;
            color: var(--sage-800);
            font-size: 0.875rem;
        }
        
        .nav {
            display: none;
            gap: 2rem;
            font-size: 0.75rem;
            font-weight: 600;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            color: var(--sage-600);
        }
        
        @media (min-width: 768px) { .nav { display: flex; } }
        
        .nav a { text-decoration: none; color: inherit; transition: color 0.2s; }
        .nav a:hover { color: var(--gold-500); }
        
        main {
            padding-top: 8rem;
            padding-bottom: 5rem;
            padding-left: 1.5rem;
            padding-right: 1.5rem;
            max-width: 56rem;
            margin: 0 auto;
        }
        
        .hero {
            text-align: center;
            margin-bottom: 4rem;
            animation: fadeIn 1.2s ease-out;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        
        @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
        }
        
        .badge {
            display: inline-block;
            padding: 0.375rem 1rem;
            border-radius: 9999px;
            background: var(--sage-100);
            color: var(--sage-600);
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 0.1em;
            text-transform: uppercase;
            margin-bottom: 1rem;
        }
        
        h1 {
            font-family: 'Montserrat', sans-serif;
            font-size: 2.5rem;
            font-weight: 700;
            color: var(--slate-900);
            margin-bottom: 1.5rem;
            line-height: 1.2;
        }
        
        @media (min-width: 768px) { h1 { font-size: 3.75rem; } }
        
        .scripture-ref {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            color: var(--sage-600);
            font-style: italic;
            font-family: 'Lora', serif;
            font-size: 1.125rem;
        }
        
        @media (min-width: 768px) { .scripture-ref { font-size: 1.25rem; } }
        
        .scripture-line {
            height: 1px;
            width: 2rem;
            background: var(--sage-100);
        }
        
        .date {
            color: var(--sage-600);
            margin-top: 1rem;
            font-size: 0.875rem;
        }
        
        .section {
            margin-bottom: 3rem;
            animation: slideUp 0.8s ease-out;
        }
        
        .section-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        
        .section-header h2 {
            font-family: 'Montserrat', sans-serif;
            font-size: 1.5rem;
            font-weight: 700;
            color: var(--sage-800);
            flex-shrink: 0;
        }
        
        .section-line {
            height: 1px;
            width: 100%;
            background: var(--sage-100);
        }
        
        .card {
            background: white;
            border-radius: 1.5rem;
            padding: 2rem;
            box-shadow: 0 1px 3px rgba(0,0,0,0.05);
            border: 1px solid var(--sage-50);
        }
        
        .card p {
            color: var(--sage-800);
            font-family: 'Lora', serif;
            font-size: 1.125rem;
            line-height: 1.8;
            white-space: pre-line;
        }
        
        .divider {
            height: 2px;
            background: linear-gradient(90deg, transparent, var(--gold-500), transparent);
            margin: 3rem 0;
        }
        
        .video-container {
            position: relative;
            width: 100%;
            padding-bottom: 56.25%;
        }
        
        .video-container iframe {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 1rem;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        
        .quote-grid {
            display: grid;
            gap: 1.5rem;
        }
        
        @media (min-width: 768px) { .quote-grid { grid-template-columns: repeat(2, 1fr); } }
        
        .quote-card {
            padding: 1.5rem;
            background: var(--parchment);
            border-radius: 0.75rem;
            border: 1px solid var(--sage-100);
            transition: border-color 0.3s;
        }
        
        .quote-card:hover { border-color: var(--gold-500); }
        
        .quote-content {
            display: flex;
            gap: 0.75rem;
        }
        
        .quote-mark {
            color: var(--gold-500);
            font-size: 1.875rem;
            font-family: 'Lora', serif;
            line-height: 1;
        }
        
        .quote-text {
            color: var(--sage-800);
            font-family: 'Lora', serif;
            font-style: italic;
            line-height: 1.6;
        }
        
        .quote-number {
            margin-top: 1rem;
            display: flex;
            justify-content: flex-end;
        }
        
        .quote-number span {
            font-size: 0.75rem;
            font-weight: 700;
            color: var(--gold-600);
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }
        
        .points-section {
            background: var(--sage-600);
            color: white;
            padding: 2rem;
            border-radius: 1.5rem;
            position: relative;
            overflow: hidden;
        }
        
        .points-section .icon-bg {
            position: absolute;
            bottom: -1rem;
            right: -1rem;
            width: 8rem;
            height: 8rem;
            opacity: 0.1;
        }
        
        .points-list {
            position: relative;
            z-index: 10;
            list-style: none;
        }
        
        .points-list li {
            margin-bottom: 1.5rem;
        }
        
        .points-list li:last-child { margin-bottom: 0; }
        
        .point-label {
            font-size: 0.75rem;
            color: var(--gold-500);
            margin-bottom: 0.25rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.1em;
        }
        
        .point-text {
            font-family: 'Lora', serif;
            font-size: 1.125rem;
        }
        
        .simple-list {
            list-style: none;
        }
        
        .simple-list li {
            display: flex;
            align-items: flex-start;
            gap: 0.75rem;
            margin-bottom: 1rem;
            padding: 1rem;
            background: var(--sage-50);
            border-radius: 0.75rem;
        }
        
        .list-bullet {
            width: 0.5rem;
            height: 0.5rem;
            background: var(--gold-500);
            border-radius: 50%;
            margin-top: 0.5rem;
            flex-shrink: 0;
        }
        
        .scripture-item {
            background: white;
            padding: 1rem 1.25rem;
            border-radius: 0.5rem;
            margin-bottom: 0.75rem;
            border-left: 3px solid var(--gold-500);
            font-family: 'Lora', serif;
            font-style: italic;
        }
        
        .prayer-item {
            background: linear-gradient(135deg, var(--sage-50) 0%, white 100%);
            padding: 1.25rem;
            border-radius: 0.75rem;
            margin-bottom: 1rem;
            display: flex;
            align-items: flex-start;
            gap: 1rem;
        }
        
        .prayer-icon {
            width: 1.5rem;
            height: 1.5rem;
            color: var(--gold-500);
            flex-shrink: 0;
        }
        
        footer {
            text-align: center;
            padding-top: 3rem;
            border-top: 1px solid var(--sage-100);
        }
        
        footer p {
            font-size: 0.875rem;
            color: var(--sage-600);
        }
        
        @media print {
            body { background: white; }
            .header { display: none; }
            main { padding-top: 2rem; }
            .card, .quote-card { box-shadow: none; border: 1px solid #ddd; }
        }
    </style>
</head>
<body>

    <header class="header">
        <div class="header-inner">
            <div class="logo">
                <div class="logo-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
                </div>
                <span class="logo-text">${data.churchName || 'SERMON STUDY'}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 2rem;">
                <nav class="nav">
                    ${youtubeId ? '<a href="#video">Video</a>' : ''}
                    <a href="#summary">Summary</a>
                    ${data.mainPoints?.length ? '<a href="#main-points">Main Points</a>' : ''}
                    ${data.quotes?.length ? '<a href="#quotes">Quotes</a>' : ''}
                    ${(data.studyQuestions?.length || data.discussionQuestions?.length) ? '<a href="#questions">Questions</a>' : ''}
                </nav>
                <button class="print-btn no-print" onclick="window.print()">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                    Print / PDF
                </button>
            </div>
        </div>
    </header>

    <main>
        <section class="hero">
            <span class="badge">Sermon Study</span>
            <h1>${data.title}</h1>
            <div class="scripture-ref">
                <div class="scripture-line"></div>
                ${data.scripture || ''}
                <div class="scripture-line"></div>
            </div>
            ${data.date ? `<p class="date">${data.date}</p>` : ''}
        </section>

        ${youtubeId ? `
        <section id="video" class="section">
            <div class="section-header">
                <h2>Watch the Sermon</h2>
                <div class="section-line"></div>
            </div>
            <div class="video-container">
                <iframe 
                    src="https://www.youtube.com/embed/${youtubeId}"
                    title="Sermon Video"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
            </div>
        </section>
        <div class="divider"></div>
        ` : ''}

        <section id="summary" class="section">
            <div class="section-header">
                <h2>Sermon Summary</h2>
                <div class="section-line"></div>
            </div>
            <div class="card">
                <p>${data.bulletinSummary}</p>
            </div>
        </section>

        ${data.mainPoints?.length ? `
        <div class="divider"></div>
        <section id="main-points" class="section">
            <div class="section-header">
                <h2>Main Points</h2>
                <div class="section-line"></div>
            </div>
            <div class="points-section">
                <ul class="points-list">
                    ${data.mainPoints.map((point, index) => `
                    <li>
                        <p class="point-label">Point ${index + 1}</p>
                        <p class="point-text">${point}</p>
                    </li>
                    `).join('')}
                </ul>
                <svg class="icon-bg" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
            </div>
        </section>
        ` : ''}

        ${data.keyScriptures?.length ? `
        <div class="divider"></div>
        <section id="scriptures" class="section">
            <div class="section-header">
                <h2>Key Scriptures</h2>
                <div class="section-line"></div>
            </div>
            <div>
                ${data.keyScriptures.map(scripture => `
                <div class="scripture-item">${scripture}</div>
                `).join('')}
            </div>
        </section>
        ` : ''}

        ${data.quotes?.length ? `
        <div class="divider"></div>
        <section id="quotes" class="section">
            <div class="section-header">
                <h2>Key Takeaways</h2>
                <div class="section-line"></div>
            </div>
            <div class="quote-grid">
                ${data.quotes.map((quote, index) => `
                <div class="quote-card">
                    <div class="quote-content">
                        <span class="quote-mark">"</span>
                        <p class="quote-text">${quote}</p>
                    </div>
                    <div class="quote-number">
                        <span>#${index + 1}</span>
                    </div>
                </div>
                `).join('')}
            </div>
        </section>
        ` : ''}

        ${data.practicalApplications?.length ? `
        <div class="divider"></div>
        <section id="applications" class="section">
            <div class="section-header">
                <h2>Practical Applications</h2>
                <div class="section-line"></div>
            </div>
            <ul class="simple-list">
                ${data.practicalApplications.map(app => `
                <li>
                    <div class="list-bullet"></div>
                    <span>${app}</span>
                </li>
                `).join('')}
            </ul>
        </section>
        ` : ''}

        ${(() => {
          const questions = data.discussionQuestions?.length ? data.discussionQuestions : data.studyQuestions;
          return questions?.length ? `
        <div class="divider"></div>
        <section id="questions" class="section">
            <div class="section-header">
                <h2>Discussion Questions</h2>
                <div class="section-line"></div>
            </div>
            <div class="points-section">
                <ul class="points-list">
                    ${questions.map((question, index) => `
                    <li>
                        <p class="point-label">Question ${index + 1}</p>
                        <p class="point-text">${question}</p>
                    </li>
                    `).join('')}
                </ul>
                <svg class="icon-bg" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"></path></svg>
            </div>
        </section>
        ` : '';
        })()}

        ${data.prayerPoints?.length ? `
        <div class="divider"></div>
        <section id="prayer" class="section">
            <div class="section-header">
                <h2>Prayer Focus</h2>
                <div class="section-line"></div>
            </div>
            <div>
                ${data.prayerPoints.map(prayer => `
                <div class="prayer-item">
                    <svg class="prayer-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                    <span>${prayer}</span>
                </div>
                `).join('')}
            </div>
        </section>
        ` : ''}

        <footer>
            <p>Created with SermonBuild</p>
        </footer>
    </main>

</body>
</html>`;
}

export function downloadStudyPageHTML(data: StudyPageData) {
  const html = generateStudyPageHTML(data);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sermon-study-${data.title.toLowerCase().replace(/\s+/g, '-')}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function openStudyPagePreview(data: StudyPageData) {
  const html = generateStudyPageHTML(data);
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
}
